import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Fuse from "fuse.js";
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { RootState } from "../../../redux/store";
import { fetchUsers } from "../../../redux/actions/userActions";
import type { RoleplayAssessedSession } from "../../../redux/actions/roleplayActions";

interface RoleplayAssessedSessionsTableProps {
  assessedSessions: RoleplayAssessedSession[];
  sessionsLoading: boolean;
  roleplayId: string;
  organizationId: string;
  initialChatSessionFilter?: string;
}

const RoleplayAssessedSessionsTable: React.FC<
  RoleplayAssessedSessionsTableProps
> = ({ assessedSessions, sessionsLoading, roleplayId, organizationId, initialChatSessionFilter }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState(initialChatSessionFilter || "");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // Get users from Redux store
  const users = useSelector((state: RootState) => state.users.users);
  const usersLoading = useSelector((state: RootState) => state.users.loading);

  // Create a map of user_id to user email for quick lookup
  const userEmailMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    users.forEach((user) => {
      map[user.id] = user.email || "No email";
    });
    return map;
  }, [users]);

  // Fetch users when component mounts
  useEffect(() => {
    if (users.length === 0 && !usersLoading) {
      dispatch(fetchUsers() as any);
    }
  }, [dispatch, users.length, usersLoading]);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Process sessions to add composite ID and formatted date
  const processedSessions = useMemo(() => {
    return assessedSessions.map((session) => ({
      ...session,
      id: session.id,
      createdDate: session._ts
        ? new Date(session._ts * 1000)
        : session.created_at
        ? new Date(session.created_at)
        : null,
      userEmail: userEmailMap[session.user_id] || "",
      overallScore: session.assessment_score,
    }));
  }, [assessedSessions, userEmailMap]);

  // Apply search filter and sort by creation time
  const filteredSessions = useMemo(() => {
    let sessions = processedSessions;

    if (searchQuery) {
      const fuse = new Fuse(sessions, {
        keys: [
          "chat_session_id",
          "user_id",
          "userEmail",
          "conversation_id",
          "source_roleplay_id",
          "state",
        ],
        threshold: 0.3,
      });
      sessions = fuse.search(searchQuery).map((result) => result.item);
    }

    // Create a copy of the array before sorting to avoid mutating read-only array
    return [...sessions].sort((a, b) => {
      if (!a.createdDate && !b.createdDate) return 0;
      if (!a.createdDate) return 1;
      if (!b.createdDate) return -1;
      return b.createdDate.getTime() - a.createdDate.getTime();
    });
  }, [searchQuery, processedSessions]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "chat_session_id",
        headerName: "Chat Session ID",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
              title={params.value as string}
            >
              {(params.value as string)?.substring(0, 8)}...
            </Typography>
          </Box>
        ),
      },
      {
        field: "id",
        headerName: "Session ID",
        flex: 1,
        minWidth: 180,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={0.5}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
              title={params.value as string}
            >
              {params.value}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopyId(params.value as string)}
              sx={{ p: 0.25 }}
            >
              {copiedId === params.value ? (
                <CheckCircleIcon
                  fontSize="small"
                  color="success"
                  sx={{ fontSize: 14 }}
                />
              ) : (
                <ContentCopyIcon fontSize="small" sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>
        ),
      },
      {
        field: "user_id",
        headerName: "User Email",
        width: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const userId = params.value;
          const userEmail = userEmailMap[userId];

          if (!userEmail && !usersLoading) {
            return (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  User not found
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              gap={0.5}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => navigate(`/dashboard/users/${userId}`)}
                sx={{
                  textTransform: "none",
                  p: 0.25,
                  fontSize: "0.75rem",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={userEmail || "Loading..."}
              >
                {userEmail || "Loading..."}
              </Button>
            </Box>
          );
        },
      },
      {
        field: "conversation_id",
        headerName: "Conversation",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Button
              size="small"
              variant="text"
              onClick={() =>
                navigate(`/dashboard/conversations/${params.value}`)
              }
              sx={{
                fontFamily: "monospace",
                textTransform: "none",
                p: 0.25,
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={params.value as string}
            >
              {(params.value as string)?.substring(0, 8)}...
            </Button>
          </Box>
        ),
      },
      {
        field: "overallScore",
        headerName: "Overall Score",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const score = params.value;
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              {score !== undefined && score !== null ? (
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: "0.75rem" }}
                >
                  {score.toFixed(1)}/10
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  -
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "createdDate",
        headerName: "Created",
        width: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {params.row.createdDate
                ? `${(params.row.createdDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}/${params.row.createdDate
                    .getDate()
                    .toString()
                    .padStart(
                      2,
                      "0"
                    )}, ${params.row.createdDate.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Button
              size="small"
              onClick={() => {
                navigate(
                  `/dashboard/roleplays/${organizationId}/${roleplayId}/sessions/${params.row?.id}?org=${organizationId}`
                );
              }}
              disabled={params.row?.state !== "ready"}
              sx={{ fontSize: "0.75rem", minWidth: "auto", px: 1 }}
            >
              View
            </Button>
          </Box>
        ),
      },
    ],
    [navigate, roleplayId, organizationId, copiedId, userEmailMap, usersLoading]
  );

  if (sessionsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (assessedSessions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No sessions have been completed for this roleplay yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <TextField
          label="Search Sessions"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          placeholder="Search by ID, email, state..."
        />
      </Box>
      <Box
        sx={{ height: "calc(100vh - 550px)", minHeight: 400, width: "100%" }}
      >
        <DataGrid
          rows={filteredSessions}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
              py: 1,
            },
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "0.875rem",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row": {
              minHeight: "52px !important",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default RoleplayAssessedSessionsTable;
